import { showMessages } from '@intercom/messenger-js-sdk';
import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { FilterToggleGroup } from '@/components/filter-toggle-group/filter-toggle-group.tsx';
import { SearchInputWithQuery } from '@/components/search-input-with-query/search-input-with-query';
import { NewStreamModal } from '@/components/streams/new-stream-modal.tsx';
import { streamFilterCategories } from '@/components/streams/stream-categories.ts';
import { getRedirectLink } from '@/routes-utils/get-redirect-link.ts';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import {
  createStreamButton,
  requestStreamButton,
  streamHeaderActions,
} from '@/routes/_app.p.marketplace.streams/streams-header/streams-header.css.ts';
import { useAppLoader } from '@/routes/_app/loader.ts';
import {
  addSearchParams,
  deleteSearchParams,
} from '@/utils/handle-search-param.ts';
import { StreamsHeaderActionsMenu } from './streams-header-actions-menu.tsx';

export const StreamHeaderFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isCustomStreams = location.pathname.startsWith(paths.customStreams);

  const activeParams = useMemo(() => {
    return [
      searchParams.get('category') && 'category',
      searchParams.get('streamState') && 'streamState',
      searchParams.get('streamType') && 'streamType',
      searchParams.get('pricingType') && 'pricingType',
    ].filter(Boolean) as string[];
  }, [searchParams]);

  return (
    <>
      <SearchInputWithQuery
        key={isCustomStreams ? 'custom-stream-search' : 'stream-search'}
        searchParamsToDelete={activeParams}
      />
      <FilterToggleGroup
        filters={streamFilterCategories}
        selectedFilter={
          searchParams.get('category') || streamFilterCategories[0].value
        }
        onReset={() =>
          setSearchParams(deleteSearchParams(searchParams, ['category']), {
            preventScrollReset: true,
          })
        }
        onSet={(value) =>
          setSearchParams(
            addSearchParams(searchParams, [{ name: 'category', value }]),
            {
              preventScrollReset: true,
            },
          )
        }
      />
    </>
  );
};

export const StreamHeaderActions = () => {
  const location = useLocation();
  const { authenticated } = useAppLoader();
  const isCustomStreams = location.pathname.startsWith(paths.customStreams);

  if (!authenticated) {
    const loginLink = getRedirectLink(paths.login, location.pathname);

    return (
      <div className={streamHeaderActions}>
        <Button className={requestStreamButton} variant="outline" asChild>
          <Link to={loginLink}>Request a stream</Link>
        </Button>

        <Button className={createStreamButton} asChild>
          <Link to={loginLink}>
            Create new {isCustomStreams ? 'custom' : ''} stream
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className={streamHeaderActions}>
      <Button
        className={requestStreamButton}
        onClick={() => showMessages()}
        variant="outline"
      >
        Request a stream
      </Button>

      <NewStreamModal
        key={isCustomStreams ? 'custom-stream-modal' : 'stream-modal'}
        trigger={
          <Button className={createStreamButton}>
            Create new {isCustomStreams ? 'custom' : ''} stream
          </Button>
        }
        customStream={isCustomStreams}
      />

      <StreamsHeaderActionsMenu />
    </div>
  );
};
